import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Package, PackageCheck, PackageSearch, Truck, X, XCircle } from "lucide-react";
import api, { formatApiError, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const STEPS = [
  { key: "Placed", icon: Package, label: "Order placed" },
  { key: "Packed", icon: PackageCheck, label: "Packed with love" },
  { key: "Shipped", icon: Truck, label: "On its way" },
  { key: "Delivered", icon: CheckCircle2, label: "Delivered" },
];

export default function TrackOrderModal() {
  const { trackOpen, setTrackOpen } = useStore();
  const [orderId, setOrderId] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const close = () => setTrackOpen(false);

  const [mode, setMode] = useState("single");
  const [orders, setOrders] = useState(null);

  const track = async () => {
    setError("");
    setOrder(null);
    setOrders(null);
    if (mode === "all") {
      if (phone.replace(/\D/g, "").length < 10) {
        setError("Enter the 10-digit mobile number used at checkout");
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get(`/orders/lookup?phone=${encodeURIComponent(phone.trim())}`);
        setOrders(data.orders);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!orderId.trim() || phone.replace(/\D/g, "").length < 10) {
      setError("Enter your order ID and 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/orders/track?order_id=${encodeURIComponent(orderId.trim())}&phone=${encodeURIComponent(phone.trim())}`
      );
      setOrder(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? STEPS.findIndex((s) => s.key === order.status) : -1;
  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm outline-none focus:border-terracotta transition-colors";

  return (
    <AnimatePresence>
      {trackOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-ink/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
          onClick={close}
          data-testid="track-order-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-cream w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] p-6 sm:p-8"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif font-black text-2xl sm:text-3xl text-ink">Track your order</h3>
              <button onClick={close} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="track-order-close" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-charcoal mb-6">Your order ID (like LJ-26-XXXXXX) is on your confirmation screen and receipt.</p>

            <div className="flex gap-2 mb-4">
              {[["single", "One order"], ["all", "All my orders"]].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => { setMode(m); setError(""); setOrder(null); setOrders(null); }}
                  className={`rounded-full px-4 py-2 font-mono text-xs tracking-wide border transition-colors ${mode === m ? "bg-ink text-cream border-ink" : "border-ink/15 text-charcoal hover:border-terracotta"}`}
                  data-testid={`track-mode-${m}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              {mode === "single" && (
                <input className={`${inputCls} font-mono`} placeholder="Order ID — LJ-26-XXXXXX" value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="track-order-id-input" />
              )}
              <input className={inputCls} placeholder="Mobile number used at checkout" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="track-order-phone-input" />
              {error && <p className="text-sm text-red-600" data-testid="track-order-error">{error}</p>}
              <button
                onClick={track}
                disabled={loading}
                className="w-full rounded-full bg-ink text-cream py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="track-order-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageSearch className="w-4 h-4" />}
                {loading ? "Finding..." : mode === "all" ? "Find My Orders" : "Track Order"}
              </button>
            </div>

            {orders && (
              <div className="mt-6 space-y-2" data-testid="track-orders-list">
                {orders.length === 0 ? (
                  <p className="text-sm text-charcoal text-center py-4 font-serif italic">No orders on this number yet — the perfect dress awaits.</p>
                ) : (
                  orders.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => { setOrder(o); setOrderId(o.id); setOrders(null); setMode("single"); }}
                      className="w-full flex items-center justify-between bg-white rounded-2xl px-4 py-3.5 text-left hover:bg-blush transition-colors"
                      data-testid={`track-order-row-${o.id}`}
                    >
                      <span>
                        <span className="block font-mono font-bold text-sm text-ink">{o.id}</span>
                        <span className="block text-xs text-charcoal/70 mt-0.5">
                          {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · {o.items.length} item{o.items.length > 1 ? "s" : ""}
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="block font-serif font-bold text-terracotta">{inr(o.total)}</span>
                        <span className="block font-mono text-[9px] uppercase tracking-widest text-sage-dark mt-0.5">{o.status}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}

            {order && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-7" data-testid="track-order-result">
                {order.payment_status === "failed" && (
                  <div className="bg-red-50 rounded-2xl p-4 mb-4 text-sm text-red-600 font-medium" data-testid="track-payment-failed">
                    Payment failed — this order is not confirmed. Please place it again.
                  </div>
                )}
                {order.status === "Awaiting Payment" && order.payment_status !== "failed" && (
                  <div className="bg-amber-50 rounded-2xl p-4 mb-4 text-sm text-amber-700 font-medium" data-testid="track-payment-pending">
                    Payment pending — your order confirms the moment payment succeeds.
                  </div>
                )}
                {order.status === "Cancelled" ? (
                  <div className="bg-red-50 rounded-2xl p-5 flex items-center gap-3">
                    <XCircle className="w-8 h-8 text-red-500" />
                    <div>
                      <p className="font-semibold text-ink">This order was cancelled</p>
                      <p className="text-xs text-charcoal mt-0.5">Write to wardrobe.baeboos@gmail.com and we'll sort it out.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between mb-6">
                    {STEPS.map((s, i) => (
                      <div key={s.key} className="flex-1 flex flex-col items-center relative" data-testid={`track-step-${s.key.toLowerCase()}`}>
                        {i > 0 && (
                          <span className={`absolute top-5 right-1/2 w-full h-0.5 ${i <= currentStep ? "bg-terracotta" : "bg-ink/10"}`} />
                        )}
                        <span className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${i <= currentStep ? "bg-terracotta text-white" : "bg-white border border-ink/10 text-charcoal/40"}`}>
                          <s.icon className="w-4 h-4" />
                        </span>
                        <span className={`mt-2 font-mono text-[9px] tracking-wider uppercase text-center ${i <= currentStep ? "text-ink" : "text-charcoal/40"}`}>
                          {s.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="bg-white rounded-2xl p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-ink" data-testid="track-order-result-id">{order.id}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest bg-peach text-terracotta-dark rounded-full px-3 py-1">
                      {order.payment_method === "cod" ? "COD" : order.payment_method}
                    </span>
                  </div>
                  {order.items.map((i) => (
                    <div key={i.product_id + i.size} className="flex justify-between text-sm">
                      <span className="text-charcoal">{i.name} · {i.size} · x{i.qty}</span>
                      <span className="font-semibold">{inr(i.price * i.qty)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-serif font-bold pt-2 border-t border-ink/10">
                    <span>Total</span>
                    <span className="text-terracotta">{inr(order.total)}</span>
                  </div>
                  <p className="text-xs text-charcoal/70 pt-1">
                    Shipping to: {order.name}, {order.city}, {order.state} — {order.pincode}
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
